import { useState, useEffect } from "react";
import {
  Search,
  Filter,
  MoreVertical,
  Mail,
  Users,
  Percent,
  Eye,
  Edit,
  Trash2,
  UserX,
  UserCheck as UserCheckIcon,
  CheckCircle,
  XCircle,
  Ban,
  Clock,
  GraduationCap,
  Building2,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/admin/ui/Button";
import { Input } from "@/components/admin/ui/Input";
import { Select } from "@/components/admin/ui/Select";
import { Badge } from "@/components/admin/ui/Badge";
import { Avatar } from "@/components/admin/ui/Avatar";
import { instructors } from "@/data/adminMock";
import { useToast } from "@/components/admin/ui/Toast";

type AdminTab = 'instructors' | 'applications';

interface Application {
  id: number;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  subject: string;
  institution: string;
  experience: string;
  qualification: string;
  bio: string;
  status: string;
  appliedAt: string;
  registeredAt: string;
}

const statusColors: Record<string, { bg: string; text: string; label: string }> = {
  pending: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', label: 'অপেক্ষমাণ' },
  approved: { bg: 'bg-emerald-50 border-emerald-200', text: 'text-emerald-700', label: 'অনুমোদিত' },
  rejected: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'প্রত্যাখ্যাত' },
  blocked: { bg: 'bg-gray-50 border-gray-300', text: 'text-gray-600', label: 'ব্লক' },
};

const subjectLabels: Record<string, string> = {
  bangla: 'বাংলা', english: 'ইংরেজি', math: 'গণিত', physics: 'পদার্থবিজ্ঞান',
  chemistry: 'রসায়ন', biology: 'জীববিজ্ঞান', ict: 'ICT', accounting: 'হিসাববিজ্ঞান',
  business: 'ব্যবসায়', economics: 'অর্থনীতি', religion: 'ইসলাম শিক্ষা',
  history: 'ইতিহাস', geography: 'ভূগোল', other: 'অন্যান্য',
  'general-science': 'সাধারণ বিজ্ঞান',
};

export function Instructors() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<AdminTab>('instructors');
  const [instructorList, setInstructorList] = useState(instructors);
  const [applications, setApplications] = useState<Application[]>([]);
  const [appLoading, setAppLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const fetchInstructors = () => {
    const ajaxUrl = (window as any).hubInstructor?.ajax_url || (window as any).ajaxurl;
    const nonce = (window as any).hubInstructor?.nonce;
    if (ajaxUrl && nonce && (window as any).jQuery) {
      (window as any).jQuery.ajax({
        url: ajaxUrl,
        method: "GET",
        data: {
          action: "hub_get_instructors",
          security: nonce
        },
        success: (res: any) => {
          if (res.success && Array.isArray(res.data)) {
            setInstructorList(res.data);
          }
        }
      });
    }
  };

  const fetchApplications = async () => {
    setAppLoading(true);
    try {
      const ajaxUrl = (window as any).hubInstructor?.ajax_url || (window as any).ajaxurl;
      const nonce = (window as any).hubInstructor?.nonce;
      if (ajaxUrl && nonce && (window as any).jQuery) {
        (window as any).jQuery.ajax({
          url: ajaxUrl,
          method: "GET",
          data: { action: "hub_get_instructor_applications", security: nonce },
          success: (res: any) => {
            if (res.success && Array.isArray(res.data)) {
              setApplications(res.data);
            }
            setAppLoading(false);
          },
          error: () => setAppLoading(false),
        });
      } else {
        // Mock data for dev
        setApplications([
          { id: 101, name: 'রাহুল আহমেদ', email: 'rahul@test.com', phone: '01711111111', avatar: '', subject: 'physics', institution: 'ঢাকা কলেজ', experience: '5-10', qualification: 'B.Sc in Physics', bio: 'পদার্থবিজ্ঞানে অভিজ্ঞ শিক্ষক।', status: 'pending', appliedAt: '2026-07-01', registeredAt: 'Jul 01, 2026' },
          { id: 102, name: 'ফারিয়া খান', email: 'faria@test.com', phone: '01822222222', avatar: '', subject: 'chemistry', institution: 'বিআইটি', experience: '3-5', qualification: 'M.Sc in Chemistry', bio: 'রসায়নে বিশেষজ্ঞ।', status: 'pending', appliedAt: '2026-07-02', registeredAt: 'Jul 02, 2026' },
        ]);
        setAppLoading(false);
      }
    } catch {
      setAppLoading(false);
    }
  };

  const handleAction = async (userId: number, action: 'approve' | 'reject' | 'block') => {
    setActionLoading(userId);
    const ajaxUrl = (window as any).hubInstructor?.ajax_url || (window as any).ajaxurl;
    const nonce = (window as any).hubInstructor?.nonce;
    const actionMap = { approve: 'hub_approve_instructor', reject: 'hub_reject_instructor', block: 'hub_block_instructor' };

    if (ajaxUrl && nonce && (window as any).jQuery) {
      (window as any).jQuery.ajax({
        url: ajaxUrl,
        method: "POST",
        data: { action: actionMap[action], security: nonce, user_id: userId },
        success: (res: any) => {
          if (res.success) {
            showToast(res.data?.message || `Successfully completed action: ${action}`, "success");
            fetchApplications();
            if (action === 'approve') fetchInstructors();
          } else {
            showToast(res.data?.message || `Failed to perform action: ${action}`, "error");
          }
          setActionLoading(null);
        },
        error: () => {
          showToast("Network request failed.", "error");
          setActionLoading(null);
        },
      });
    } else {
      // Mock
      await new Promise(r => setTimeout(r, 500));
      const statusMap = { approve: 'approved', reject: 'rejected', block: 'blocked' };
      setApplications(prev => prev.map(a => a.id === userId ? { ...a, status: statusMap[action] } : a));
      showToast(`Mock: Successfully completed action: ${action}`, "success");
      setActionLoading(null);
    }
  };

  useEffect(() => {
    fetchInstructors();
    (window as any).refreshInstructors = fetchInstructors;
    return () => {
      delete (window as any).refreshInstructors;
    };
  }, []);

  useEffect(() => {
    if (activeTab === 'applications') {
      fetchApplications();
    }
  }, [activeTab]);

  const [selected, setSelected] = useState<number[]>([]);
  const [search, setSearch] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [bulkAction, setBulkAction] = useState("");
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  const allCourses = Array.from(
    new Set(instructors.flatMap((i) => i.assignedCourses))
  );

  const filteredInstructors = instructorList.filter((inst) => {
    const query = search.toLowerCase();
    const matchesSearch =
      inst.name.toLowerCase().includes(query) ||
      inst.email.toLowerCase().includes(query) ||
      inst.assignedCourses.some((c) => c.toLowerCase().includes(query));
    const matchesCourse = selectedCourse
      ? inst.assignedCourses.includes(selectedCourse)
      : true;
    const matchesStatus = selectedStatus
      ? inst.status === selectedStatus
      : true;
    return matchesSearch && matchesCourse && matchesStatus;
  });

  const toggleAll = () => {
    if (selected.length === filteredInstructors.length) {
      setSelected([]);
    } else {
      setSelected(filteredInstructors.map((i) => i.id));
    }
  };

  const toggleOne = (id: number) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleBulkApply = () => {
    if (!bulkAction || selected.length === 0) return;
    if (bulkAction === "activate") {
      setInstructorList((prev) =>
        prev.map((i) =>
          selected.includes(i.id) ? { ...i, status: "Active" } : i
        )
      );
    } else if (bulkAction === "deactivate") {
      setInstructorList((prev) =>
        prev.map((i) =>
          selected.includes(i.id) ? { ...i, status: "Inactive" } : i
        )
      );
    } else if (bulkAction === "delete") {
      setInstructorList((prev) =>
        prev.filter((i) => !selected.includes(i.id))
      );
    }
    setSelected([]);
    setBulkAction("");
  };

  const handleToggleStatus = (id: number) => {
    setInstructorList((prev) =>
      prev.map((i) =>
        i.id === id
          ? { ...i, status: i.status === "Active" ? "Inactive" : "Active" }
          : i
      )
    );
    setOpenMenuId(null);
  };

  const handleDelete = (id: number) => {
    setInstructorList((prev) => prev.filter((i) => i.id !== id));
    setOpenMenuId(null);
  };

  const pendingCount = applications.filter(a => a.status === 'pending').length;

  return (
    <div className="p-6 lg:p-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1F2C]">Instructors</h1>
          <p className="text-sm text-[#5A6A85] mt-1">
            Total {filteredInstructors.length} registered instructors
          </p>
        </div>
        <Button 
          id="hub-add-instructor-btn" 
          leftIcon={<span className="text-lg leading-none">+</span>}
        >
          Add New Instructor
        </Button>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('instructors')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all ${
            activeTab === 'instructors'
              ? 'border-[#6C5CE7] text-[#6C5CE7]'
              : 'border-transparent text-[#5A6A85] hover:text-[#1A1F2C]'
          }`}
        >
          Instructors
        </button>
        <button
          onClick={() => setActiveTab('applications')}
          className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-all flex items-center gap-2 ${
            activeTab === 'applications'
              ? 'border-[#6C5CE7] text-[#6C5CE7]'
              : 'border-transparent text-[#5A6A85] hover:text-[#1A1F2C]'
          }`}
        >
          আবেদনকারী (Applications)
          {pendingCount > 0 && (
            <span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center">{pendingCount}</span>
          )}
        </button>
      </div>

      {/* Applications Tab Content */}
      {activeTab === 'applications' && (
        <div className="space-y-4">
          {appLoading ? (
            <div className="text-center py-16 text-sm text-[#5A6A85]">Loading applications...</div>
          ) : applications.length === 0 ? (
            <div className="text-center py-16">
              <GraduationCap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-[#5A6A85]">No instructor applications yet.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {applications.map((app) => {
                const st = statusColors[app.status] || statusColors.pending;
                return (
                  <div key={app.id} className="bg-white border border-[#E2E8F0] rounded-xl p-5 hover:shadow-md transition-shadow">
                    <div className="flex flex-col md:flex-row md:items-start gap-4">
                      {/* Avatar & Info */}
                      <div className="flex items-start gap-4 flex-1 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#6C5CE7]/10 to-[#a29bfe]/10 border border-[#6C5CE7]/20 flex items-center justify-center shrink-0">
                          {app.avatar ? (
                            <img src={app.avatar} alt={app.name} className="w-full h-full rounded-xl object-cover" />
                          ) : (
                            <GraduationCap className="w-6 h-6 text-[#6C5CE7]" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-sm font-bold text-[#1A1F2C] truncate">{app.name}</h3>
                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${st.bg} ${st.text}`}>
                              {st.label}
                            </span>
                          </div>
                          <p className="text-xs text-[#5A6A85] truncate">{app.email} · {app.phone}</p>
                          <div className="flex flex-wrap gap-3 mt-2">
                            <span className="inline-flex items-center gap-1 text-xs text-[#5A6A85]">
                              <BookOpen className="w-3 h-3" /> {subjectLabels[app.subject] || app.subject}
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs text-[#5A6A85]">
                              <Building2 className="w-3 h-3" /> {app.institution}
                            </span>
                            {app.experience && (
                              <span className="inline-flex items-center gap-1 text-xs text-[#5A6A85]">
                                <Clock className="w-3 h-3" /> {app.experience} years
                              </span>
                            )}
                          </div>
                          {app.bio && <p className="text-xs text-[#5A6A85] mt-2 line-clamp-2">{app.bio}</p>}
                          {app.qualification && <p className="text-xs text-[#5A6A85] mt-1 italic">📎 {app.qualification}</p>}
                          <p className="text-[10px] text-gray-400 mt-2">Applied: {app.registeredAt}</p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      {app.status === 'pending' && (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handleAction(app.id, 'approve')}
                            disabled={actionLoading === app.id}
                            className="px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium flex items-center gap-1.5 transition-all disabled:opacity-50"
                          >
                            <CheckCircle className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button
                            onClick={() => handleAction(app.id, 'reject')}
                            disabled={actionLoading === app.id}
                            className="px-3 py-2 rounded-lg bg-red-500 hover:bg-red-600 text-white text-xs font-medium flex items-center gap-1.5 transition-all disabled:opacity-50"
                          >
                            <XCircle className="w-3.5 h-3.5" /> Reject
                          </button>
                          <button
                            onClick={() => handleAction(app.id, 'block')}
                            disabled={actionLoading === app.id}
                            className="px-3 py-2 rounded-lg bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium flex items-center gap-1.5 transition-all disabled:opacity-50"
                          >
                            <Ban className="w-3.5 h-3.5" /> Block
                          </button>
                        </div>
                      )}
                      {app.status === 'approved' && (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handleAction(app.id, 'block')}
                            disabled={actionLoading === app.id}
                            className="px-3 py-2 rounded-lg bg-gray-500 hover:bg-gray-600 text-white text-xs font-medium flex items-center gap-1.5 transition-all disabled:opacity-50"
                          >
                            <Ban className="w-3.5 h-3.5" /> Block
                          </button>
                        </div>
                      )}
                      {(app.status === 'rejected' || app.status === 'blocked') && (
                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handleAction(app.id, 'approve')}
                            disabled={actionLoading === app.id}
                            className="px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-medium flex items-center gap-1.5 transition-all disabled:opacity-50"
                          >
                            <CheckCircle className="w-3.5 h-3.5" /> Re-approve
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Existing Instructors Tab */}
      {activeTab === 'instructors' && (
      <div className="bg-white border border-[#E2E8F0] rounded-xl shadow-sm overflow-hidden">
        {/* Filter Toolbar */}
        <div className="p-4 border-b border-gray-100 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Select
              options={[
                { value: "", label: "Bulk Action" },
                { value: "activate", label: "Activate" },
                { value: "deactivate", label: "Deactivate" },
                { value: "delete", label: "Delete" },
              ]}
              value={bulkAction}
              onChange={(val) => setBulkAction(val)}
              className="w-40"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={handleBulkApply}
              disabled={selected.length === 0}
            >
              Apply
            </Button>
            {selected.length > 0 && (
              <span className="text-xs text-[#5A6A85]">
                {selected.length} selected
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant={filtersOpen ? "primary" : "outline"}
              size="sm"
              leftIcon={<Filter className="h-4 w-4" />}
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              Filters
            </Button>
            <Input
              placeholder="Search instructors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search className="h-4 w-4" />}
              containerClassName="w-64"
            />
          </div>
        </div>

        {/* Dynamic Filters panel */}
        {filtersOpen && (
          <div className="p-4 bg-gray-50/70 border-b border-gray-100 flex flex-wrap gap-4 items-center">
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-[#5A6A85] uppercase tracking-wider">
                Assigned Course
              </span>
              <select
                value={selectedCourse}
                onChange={(e) => setSelectedCourse(e.target.value)}
                className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-xs text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/20 focus:border-[#ff0000]"
              >
                <option value="">All Courses</option>
                {allCourses.map((course) => (
                  <option key={course} value={course}>
                    {course}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="text-xs font-semibold text-[#5A6A85] uppercase tracking-wider">
                Status
              </span>
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="rounded-lg border border-[#E2E8F0] bg-white px-3 py-1.5 text-xs text-[#374151] focus:outline-none focus:ring-2 focus:ring-[#ff0000]/20 focus:border-[#ff0000]"
              >
                <option value="">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            {(selectedCourse || selectedStatus) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedCourse("");
                  setSelectedStatus("");
                }}
                className="mt-5 text-[#5A6A85] hover:text-[#ff0000]"
              >
                Reset Filters
              </Button>
            )}
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gray-50 text-[#5A6A85]">
              <tr>
                <th className="px-5 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={
                      selected.length === filteredInstructors.length &&
                      filteredInstructors.length > 0
                    }
                    onChange={toggleAll}
                    className="rounded border-gray-300 text-[#ff0000] focus:ring-[#ff0000]/30"
                  />
                </th>
                <th className="px-5 py-3 font-medium">Instructor</th>
                <th className="px-5 py-3 font-medium">Assigned Courses</th>
                <th className="px-5 py-3 font-medium">Students</th>
                <th className="px-5 py-3 font-medium">Revenue Share</th>
                <th className="px-5 py-3 font-medium">Joined</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {filteredInstructors.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-5 py-8 text-center text-[#5A6A85]"
                  >
                    No instructors found matching the search criteria.
                  </td>
                </tr>
              ) : (
                filteredInstructors.map((instructor) => (
                  <tr
                    key={instructor.id}
                    className="hover:bg-gray-50/60 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <input
                        type="checkbox"
                        checked={selected.includes(instructor.id)}
                        onChange={() => toggleOne(instructor.id)}
                        className="rounded border-gray-300 text-[#ff0000] focus:ring-[#ff0000]/30"
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3 min-w-[220px]">
                        <Avatar
                          src={instructor.avatar}
                          name={instructor.name}
                        />
                        <div>
                          <p className="font-semibold text-[#1A1F2C]">
                            {instructor.name}
                          </p>
                          <p className="text-xs text-[#5A6A85] flex items-center gap-1 mt-0.5">
                            <Mail className="h-3 w-3" /> {instructor.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex flex-wrap gap-1.5 max-w-xs">
                        {instructor.assignedCourses
                          .slice(0, 2)
                          .map((course) => (
                            <Badge
                              key={course}
                              variant="blue"
                              className="truncate max-w-[160px]"
                            >
                              {course}
                            </Badge>
                          ))}
                        {instructor.assignedCourses.length > 2 && (
                          <Badge variant="gray">
                            +{instructor.assignedCourses.length - 2} more
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-[#374151]">
                        <Users className="h-3.5 w-3.5 text-[#94A3B8]" />
                        <span className="font-medium">
                          {instructor.totalStudents}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-[#374151]">
                        <Percent className="h-3.5 w-3.5 text-[#94A3B8]" />
                        <span className="font-medium">
                          {instructor.revenueShare}%
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-[#5A6A85]">
                      {instructor.registrationDate}
                    </td>
                    <td className="px-5 py-4">
                      <Badge
                        variant={
                          instructor.status === "Active" ? "green" : "gray"
                        }
                      >
                        {instructor.status}
                      </Badge>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="hub-view-instructor-btn"
                          data-id={instructor.id}
                          leftIcon={<Eye className="h-4 w-4" />}
                        >
                          View
                        </Button>
                        <div className="relative">
                          <button
                            onClick={() =>
                              setOpenMenuId(
                                openMenuId === instructor.id
                                  ? null
                                  : instructor.id
                              )
                            }
                            className="p-1.5 text-[#94A3B8] hover:text-[#374151] hover:bg-gray-100 rounded-lg transition-colors"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </button>
                          {openMenuId === instructor.id && (
                            <>
                              <div
                                className="fixed inset-0 z-40"
                                onClick={() => setOpenMenuId(null)}
                              />
                              <div className="absolute right-0 top-full mt-1 w-44 bg-white border border-[#E2E8F0] rounded-lg shadow-lg z-50 py-1 animate-in fade-in slide-in-from-top-1 duration-150">
                                <button
                                  onClick={() => {
                                    setOpenMenuId(null);
                                  }}
                                  className="hub-edit-instructor-btn w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors"
                                  data-id={instructor.id}
                                >
                                  <Edit className="h-3.5 w-3.5" />
                                  Edit Profile
                                </button>
                                <button
                                  onClick={() =>
                                    handleToggleStatus(instructor.id)
                                  }
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-[#374151] hover:bg-gray-50 transition-colors"
                                >
                                  {instructor.status === "Active" ? (
                                    <>
                                      <UserX className="h-3.5 w-3.5" />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <UserCheckIcon className="h-3.5 w-3.5" />
                                      Activate
                                    </>
                                  )}
                                </button>
                                <hr className="my-1 border-gray-100" />
                                <button
                                  onClick={() => handleDelete(instructor.id)}
                                  className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Remove
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}
    </div>
  );
}
